import Segment from '@/creditflowresearch/domain/Segment';
import type { IDictionaryIndustry } from '@/creditflowresearch/domain/vo/IDictionary';
import normalizeGroupSelection from '@/creditflowresearch/application/BondScreener/normalizeGroupSelection';
import { getActiveSegments, getSegmentSubindustryIds } from './segmentIndustryMapping';

export interface ISegmentDerivedFilters {
  includedIndustryIds: string[],
  includedSubindustryIds: string[],
  selectedSubindustryIds: string[],
}

type TSegmentSubindustryIds = ReturnType<typeof getSegmentSubindustryIds>;

const toSegmentSubindustryIds = (industries: IDictionaryIndustry[]): TSegmentSubindustryIds =>
  getSegmentSubindustryIds(industries.flatMap((industry) => industry.subindustries));

export const NO_SEGMENTS_SELECTED: ISegmentDerivedFilters = {
  includedIndustryIds: [],
  includedSubindustryIds: [],
  selectedSubindustryIds: [],
};

export const parseSegmentsQuery = (
  segmentsQuery: string,
  industries: IDictionaryIndustry[],
): ISegmentDerivedFilters => {
  if (industries.length === 0) {
    return NO_SEGMENTS_SELECTED;
  }

  const segmentSubindustryIds = toSegmentSubindustryIds(industries);
  const activeSegmentIds = new Set(Segment.getIds(segmentsQuery));

  const selectedSubindustryIds = segmentSubindustryIds
    .filter((segment) => activeSegmentIds.has(segment.id))
    .flatMap((segment) => segment.ids);

  const groups = industries.map((industry) => ({
    id: industry.id,
    childIds: industry.subindustries.map((sub) => sub.id),
  }));
  const { groupIds: includedIndustryIds, childIds: includedSubindustryIds } =
    normalizeGroupSelection(selectedSubindustryIds, groups);

  return { includedIndustryIds, includedSubindustryIds, selectedSubindustryIds };
};

export const buildSegmentsQueryParam = (
  selectedSubindustryIds: string[],
  industries: IDictionaryIndustry[],
): string => {
  if (industries.length === 0 || selectedSubindustryIds.length === 0) {
    return '';
  }

  const segmentSubindustryIds = toSegmentSubindustryIds(industries);

  return getActiveSegments(segmentSubindustryIds, selectedSubindustryIds)
    .filter((segment) => segment.isActive)
    .map((segment) => segment.label)
    .join(',');
};
